import type { ValueResolver } from '@examdojo/core/widget';
import type { PortGetter, PortMap, PortValue, PointPort, EdgePort, ScalarPort, ElementPort } from './port.model';

// Port names that return PointPort values (convention-based detection)
const KNOWN_POINT_PORTS = new Set([
  'center',
  'foot',
  'midpoint',
  'intersection',
  'midpoint1',
  'midpoint2',
  'from',
  'to',
  'point',
]);

/**
 * Centralized port registry for the GeometryPad widget.
 *
 * Each element registers its ports after creation. Ports are stored as
 * lazy getter functions so that reactive elements (dependent on draggable
 * points) return updated values automatically.
 *
 * Port paths use dot notation: "elementId.portName"
 * e.g., "t1.point.A", "r1.w", "t1.edge.AB"
 *
 * Point and edge ports are automatically decomposed into scalar sub-ports:
 * - PointPort "point.A" → "point.A.x", "point.A.y"
 * - EdgePort "edge.AB" → "edge.AB.start.x", "edge.AB.start.y",
 *   "edge.AB.end.x", "edge.AB.end.y", "edge.AB.length"
 */
export class PortRegistry implements ValueResolver {
  private readonly elements = new Map<string, PortMap>();
  private readonly variables = new Map<string, () => number>();

  /**
   * Register all ports for an element.
   * Automatically decomposes PointPort and EdgePort entries into scalar sub-ports.
   */
  register(elementId: string, ports: PortMap): void {
    const extended = this.decomposePortMap(ports);
    this.elements.set(elementId, extended);
  }

  /**
   * Register a variable (bare-name scalar) that expressions can reference.
   * Variables are resolved by name without dot notation (e.g., 'k' instead of 'element.port').
   */
  registerVariable(name: string, getter: () => number): void {
    this.variables.set(name, getter);
  }

  /**
   * Check if an element has been registered.
   */
  has(elementId: string): boolean {
    return this.elements.has(elementId);
  }

  /**
   * Check if a specific port path or variable exists.
   * Also satisfies the ValueResolver.has() interface.
   */
  hasPort(path: string): boolean {
    const dotIndex = path.indexOf('.');
    if (dotIndex === -1) {
      return this.variables.has(path);
    }
    const { elementId, portName } = this.parsePath(path);
    const ports = this.elements.get(elementId);
    return ports !== undefined && portName in ports;
  }

  /**
   * Resolve a port path to its current value.
   * @throws Error if the port path is invalid or not found.
   */
  resolve(path: string): PortValue {
    const getter = this.getGetter(path);
    return getter();
  }

  /**
   * Resolve a port path as a scalar number.
   * @throws Error if the port value is not a number.
   */
  resolveNumber(path: string): number {
    const value = this.resolve(path);
    if (typeof value !== 'number') {
      throw new Error(`Port "${path}" is not a scalar number, got: ${typeof value}`);
    }
    return value as ScalarPort;
  }

  /**
   * Resolve a port path as a 2D point.
   * @throws Error if the port value is not a point array.
   */
  resolvePoint(path: string): PointPort {
    const value = this.resolve(path);
    if (!Array.isArray(value) || value.length !== 2) {
      throw new Error(`Port "${path}" is not a point, got: ${JSON.stringify(value)}`);
    }
    return value as PointPort;
  }

  /**
   * Resolve a port path as a 2D point.
   * If the port resolves to an EdgePort, returns its midpoint.
   * @throws Error if the port value is not a point or edge.
   */
  resolvePointOrEdgeMidpoint(path: string): PointPort {
    const value = this.resolve(path);
    if (Array.isArray(value) && value.length === 2) {
      return value as PointPort;
    }
    if (typeof value === 'object' && value !== null && 'start' in value && 'end' in value) {
      const edge = value as EdgePort;
      return [(edge.start[0] + edge.end[0]) / 2, (edge.start[1] + edge.end[1]) / 2];
    }
    throw new Error(`Port "${path}" is not a point or edge, got: ${JSON.stringify(value)}`);
  }

  /**
   * Resolve a port path as an edge (pair of points).
   * @throws Error if the port value is not an edge object.
   */
  resolveEdge(path: string): EdgePort {
    const value = this.resolve(path);
    if (typeof value !== 'object' || value === null || !('start' in value) || !('end' in value)) {
      throw new Error(`Port "${path}" is not an edge, got: ${JSON.stringify(value)}`);
    }
    return value as EdgePort;
  }

  /**
   * Resolve a port path as a raw element reference (for glider targets etc).
   */
  resolveElement(path: string): ElementPort {
    return this.resolve(path);
  }

  /**
   * Get the getter function for a port or variable.
   * Bare names (no dot) resolve as variables; dotted paths resolve as element ports.
   */
  getGetter(path: string): PortGetter {
    const dotIndex = path.indexOf('.');
    if (dotIndex === -1) {
      // Bare name → variable lookup
      const variable = this.variables.get(path);
      if (variable) {
        return variable as PortGetter;
      }
      throw new Error(
        `"${path}" not found — not a port path (missing dot) or registered variable. Variables: [${[
          ...this.variables.keys(),
        ].join(', ')}]`,
      );
    }

    const { elementId, portName } = this.parsePath(path);
    const ports = this.elements.get(elementId);
    if (!ports) {
      throw new Error(
        `Element "${elementId}" not found in port registry. Available: [${[...this.elements.keys()].join(', ')}]`,
      );
    }
    const getter = ports[portName];
    if (!getter) {
      throw new Error(
        `Port "${portName}" not found on element "${elementId}". Available: [${Object.keys(ports).join(', ')}]`,
      );
    }
    return getter;
  }

  /**
   * Get all port names for an element.
   */
  getPortNames(elementId: string): string[] {
    const ports = this.elements.get(elementId);
    return ports ? Object.keys(ports) : [];
  }

  /**
   * Get all registered element IDs.
   */
  getElementIds(): string[] {
    return [...this.elements.keys()];
  }

  /**
   * Decompose point and edge ports into scalar sub-ports (.x, .y, .length, etc.)
   */
  private decomposePortMap(ports: PortMap): PortMap {
    const extended: PortMap = { ...ports };

    for (const [name, getter] of Object.entries(ports)) {
      if (this.isPointPortName(name)) {
        extended[`${name}.x`] = () => (getter() as PointPort)[0];
        extended[`${name}.y`] = () => (getter() as PointPort)[1];
      } else if (this.isEdgePortName(name)) {
        extended[`${name}.start.x`] = () => (getter() as EdgePort).start[0];
        extended[`${name}.start.y`] = () => (getter() as EdgePort).start[1];
        extended[`${name}.end.x`] = () => (getter() as EdgePort).end[0];
        extended[`${name}.end.y`] = () => (getter() as EdgePort).end[1];
        extended[`${name}.length`] = () => {
          const e = getter() as EdgePort;
          const dx = e.end[0] - e.start[0];
          const dy = e.end[1] - e.start[1];
          return Math.sqrt(dx * dx + dy * dy);
        };
        extended[`${name}.midpoint`] = () => {
          const e = getter() as EdgePort;
          return [(e.start[0] + e.end[0]) / 2, (e.start[1] + e.end[1]) / 2] as PointPort;
        };
        extended[`${name}.midpoint.x`] = () => {
          const e = getter() as EdgePort;
          return (e.start[0] + e.end[0]) / 2;
        };
        extended[`${name}.midpoint.y`] = () => {
          const e = getter() as EdgePort;
          return (e.start[1] + e.end[1]) / 2;
        };
      }
    }

    return extended;
  }

  private isPointPortName(name: string): boolean {
    return (
      name.startsWith('point.') ||
      name.startsWith('point[') ||
      name.startsWith('angleCenter.') ||
      name.startsWith('angleCenter[') ||
      KNOWN_POINT_PORTS.has(name)
    );
  }

  private isEdgePortName(name: string): boolean {
    return name.startsWith('edge.') || name.startsWith('edge[');
  }

  /**
   * Parse a dot-notation port path into element ID and port name.
   * "t1.point.A" → { elementId: "t1", portName: "point.A" }
   * "r1.w" → { elementId: "r1", portName: "w" }
   */
  private parsePath(path: string): { elementId: string; portName: string } {
    const dotIndex = path.indexOf('.');
    if (dotIndex === -1) {
      throw new Error(`Invalid port path "${path}": must contain at least one dot (elementId.portName)`);
    }

    const elementId = path.substring(0, dotIndex);
    const portName = path.substring(dotIndex + 1);

    if (!elementId || !portName) {
      throw new Error(`Invalid port path "${path}": element ID and port name must not be empty`);
    }

    return { elementId, portName };
  }
}
